import { addDays, format, isBefore, isSameDay } from "date-fns";
import cloneDeep from "lodash/cloneDeep";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert } from "react-bootstrap";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";
import { FormValidation } from "react-jsonschema-form";
import { useHistory } from "react-router";
import styled from "styled-components";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { usePropertyInventoryData } from "../../components/inventory-provider";
import { usePropertyLocales } from "../../components/property-locale-provider";
import actions from "../../lib/actions";
import { useConfig } from "../../lib/config";
import { useHttpClient } from "../../lib/http";
import { InventoryEntry } from "../../lib/model";
import CrudForm, { FormConfiguration } from "../shared/crud/form";
import { defaultCrudMessages } from "../shared/crud/simple-crud";
import { ModificationPriceManagement } from "../shared/widgets/modificationPriceManagement/modificationPriceManagement";
import Section from "../shared/widgets/section";
import { FormContainer } from "./form-style";
import { Amount, DailyValue, Reservation, SelectedService, TransformedReservation } from "./model";
import schemaJSON from "./schema.json";
import { FEATURE_TAGS } from "../witadmin/Common/constants";
import { BundleDTO, fromBundleDTOToRoomReservationBundle } from "./mapper";

const messages = {
  ...defaultCrudMessages,
  entityName: {
    id: "modification.entityName",
    defaultMessage: "Edit booking",
  },
  dateMustBeAfter: {
    id: "modification.translation.dateMustBeAfter",
    defaultMessage: "The end date must be greater than the start date",
  },
  mustNotBeZero: {
    id: "modification.translation.mustNotBeZero",
    defaultMessage: "All the prices must be higher than 0",
  },
  depositMustBeZeroOrMore: {
    id: "modification.translation.depositMustBeZeroOrMore",
    defaultMessage: "The deposit cannot be less than 0",
  },
  emailNotValid: {
    id: "modification.translation.emailNotValid",
    defaultMessage: "Please enter a valid email address",
  },
  phoneNotValid: {
    id: "modification.translation.phoneNotValid",
    defaultMessage: "Please enter a valid phone address",
  },
  notModifiable: {
    id: "modification.translation.notModifiable",
    defaultMessage: "This reservation is not modifiable, please contact our support team",
  },
};

const StyledAlert = styled(Alert)`
  margin-top: 20px;
`;

function extractRoomStayId() {
  const slashPLength = 9;
  const pathname = window?.location?.pathname;
  const pStart = pathname?.indexOf("/P");
  return pathname.substring(pStart + 1, pStart + 1 + slashPLength);
}

function ReservationModificationForm({ propertyTicker }: { propertyTicker: string }) {
  const [schema, setSchema] = useState(schemaJSON);
  const [isPackage, setIsPackage] = useState(false);
  const config = useConfig();
  const httpClient = useHttpClient(config.apiEndpoint);
  const inventories = usePropertyInventoryData();
  const intl = useIntl();
  const history = useHistory()

  useEffect(() => {
    if (inventories?.length && inventories[0] && inventories[0].inventoryTickers) {
      const tempSchema: any = { ...schemaJSON };
      tempSchema.properties.priceManagement = {
        type: "object",
        properties: {
          dailyValues: {
            type: "array",
          },
          selectedServices: {
            type: "array",
          },
          quantity: {
            type: "number",
          },
          checkIn: { type: "string" },
          checkOut: { type: "string" },
          comments: { type: "string" },
          internalNotes: { type: "string" },
          inventoryTicker: {
            enum: inventories[0].inventoryTickers?.map((inv: InventoryEntry) => inv.ticker),
            enumNames: inventories[0]?.inventoryTickers?.map(
              (inv: InventoryEntry) =>
                `${inv.accommodation.name} ${inv.configuration.name} ${inv.mealPlan.name} ${inv.condition.name} (${inv.ticker})`
            ),
          },
        },
      };
      setSchema(tempSchema);
    }
  }, [inventories]);

  const onGet = useCallback(async () => {
    const pID = extractRoomStayId();
    const response = await httpClient.get<Reservation>(
      `/api/properties/${propertyTicker}/reservations/${pID}/masked`
    );

    if (
      response?.roomReservations[0]?.packageTicker ||
      response?.tags?.includes(FEATURE_TAGS.dynamic_packages)
    ) {
      setIsPackage(true);
    }

    const transformedResponse: TransformedReservation = { ...response };

    if (transformedResponse) {
      transformedResponse.name = transformedResponse?.customer.name;
      transformedResponse.lastName = transformedResponse?.customer.lastName;
      transformedResponse.confirmationId = transformedResponse?.roomReservations[0].confirmationId;
      transformedResponse.email = transformedResponse?.customer.email;
      transformedResponse.country = transformedResponse?.customer.country;
      transformedResponse.phone = transformedResponse?.customer.phone;
      transformedResponse.inventoryTicker =
        transformedResponse?.roomReservations[0].inventoryTicker;
      transformedResponse.priceManagement = {
        dailyValues: Object.entries(transformedResponse?.roomReservations[0].dailyValues).reduce(
          (accum: DailyValue[], curr) => [
            ...accum,
            {
              ticker: curr[0],
              amount: Object.keys(transformedResponse?.roomReservations[0].dailyDiscounts).length
                ? (Number(Number(curr[1].amount).toFixed(2)) +
                    (transformedResponse?.roomReservations[0].dailyDiscounts[curr[0]] || 0)) /
                  transformedResponse?.roomReservations[0].quantity
                : Number(Number(curr[1].amount).toFixed(2)) /
                  transformedResponse?.roomReservations[0].quantity,
            },
          ],
          []
        ),
        selectedServices: transformedResponse?.roomReservations[0].selectedServices.map(service => {
          const newService: SelectedService = { amount: 0, name: "", ticker: "" };
          newService.amount = (service.amount as Amount).amount;
          newService.name = service.name;
          newService.ticker = service.ticker;
          return newService;
        }),
        quantity: transformedResponse?.roomReservations[0].quantity,
        checkIn: transformedResponse?.roomReservations[0].checkIn,
        checkOut: transformedResponse?.roomReservations[0].checkOut,
        comments: transformedResponse?.roomReservations[0].comments,
        internalNotes: transformedResponse?.roomReservations[0].internalNotes,
        guaranteeAmount: Number(
          Number(transformedResponse?.roomReservations[0].guaranteeAmount.amount).toFixed(2)
        ),
        totalAmount: Number(
          Number(transformedResponse?.roomReservations[0].totalAmount.amount).toFixed(2)
        ),
        inventoryTicker: transformedResponse?.roomReservations[0].inventoryTicker,
        originalPromoCodes: transformedResponse?.roomReservations[0].discounts,
      };
      if (transformedResponse?.priceManagement) {
        transformedResponse.priceManagement.originalDailyValues =
          transformedResponse?.priceManagement?.dailyValues;
      }
    }
    return transformedResponse;
  }, [httpClient, propertyTicker]);

  const onUpdate: (entity: TransformedReservation) => Promise<void> = useCallback(
    async entity => {
      const localEntity = cloneDeep(entity);

      localEntity.customer = {
        ...localEntity.customer,
        name: localEntity.name!!,
        lastName: localEntity.lastName!!,
        email: localEntity.email!!,
        country: localEntity.country!!,
        phone: localEntity.phone!!,
      };
      const dailyValues = localEntity.priceManagement?.dailyValues;
      const lastDay =
        (Array.isArray(dailyValues) &&
          dailyValues[dailyValues.length - 1] &&
          dailyValues[dailyValues.length - 1].ticker.replace(/-/g, "/")) ||
        "";
      const checkOut = format(addDays(new Date(lastDay), 1), "yyyy-MM-dd");

      localEntity.roomReservations[0].comments = localEntity.priceManagement?.comments!!;
      localEntity.roomReservations[0].internalNotes = localEntity.priceManagement?.internalNotes!!;
      localEntity.roomReservations[0].guaranteeAmount.amount = localEntity.priceManagement?.guaranteeAmount!!;
      localEntity.roomReservations[0].inventoryTicker = localEntity.priceManagement?.inventoryTicker!!;
      localEntity.roomReservations[0].checkIn = (dailyValues && dailyValues[0].ticker) || "";
      localEntity.roomReservations[0].checkOut = checkOut;
      localEntity.roomReservations[0].quantity = localEntity.priceManagement?.quantity!!;
      localEntity.roomReservations[0].totalAmount = {
        amount: localEntity.priceManagement?.totalAmount!!,
        currency: "EUR",
      };

      localEntity.roomReservations[0].dailyValues = localEntity.priceManagement?.dailyValues.reduce(
        (accum, curr) => ({
          ...accum,
          [curr.ticker as string]: {
            amount: curr.amount * localEntity.priceManagement?.quantity!!,
            currency: "EUR",
          },
        }),
        {}
      )!!;

      const remainingTickers = localEntity?.priceManagement?.selectedServices.map(it => it.ticker);

      localEntity.roomReservations[0].selectedServices = localEntity.roomReservations[0].selectedServices.filter(
        val => {
          return remainingTickers?.includes(val.ticker);
        }
      );

      localEntity.roomReservations[0].selectedServices.forEach((item, index) => {
        localEntity.roomReservations[0].selectedServices[index].amount = {
          ...(localEntity.roomReservations[0].selectedServices[index].amount as Amount),
          amount: localEntity?.priceManagement?.selectedServices.find(
            it => it.ticker === item.ticker
          )?.amount as number,
        };
      });

      const inventory = inventories?.[0].inventoryTickers?.find(
        it => it.ticker === localEntity.priceManagement?.inventoryTicker
      );

      localEntity.roomReservations[0].accommodationTicker = inventory?.accommodation?.ticker || "";
      localEntity.roomReservations[0].configurationTicker = inventory?.configuration?.ticker || "";
      localEntity.roomReservations[0].mealPlanTicker = inventory?.mealPlan?.ticker || "";
      localEntity.roomReservations[0].conditionTicker = inventory?.condition?.ticker || "";

      localEntity.affiliate = entity.affiliate;
      localEntity.status = entity.status;

      delete localEntity.inventoryTicker;
      delete localEntity.datePicker;
      delete localEntity.confirmationId;
      delete localEntity.name;
      delete localEntity.lastName;
      delete localEntity.email;
      delete localEntity.priceManagement;
      delete localEntity.country;
      delete localEntity.phone;

      if (localEntity.roomReservations[0].bundle) {
        localEntity.roomReservations[0].bundle = fromBundleDTOToRoomReservationBundle(localEntity.roomReservations[0].bundle as BundleDTO);
      }
      await httpClient
        .put<Reservation, Reservation>(`/api/properties/admin/reservations`, localEntity)
        .then(() => {
          history.push(`/${propertyTicker}/reservations`);
        });
    },
    [inventories, httpClient, history, propertyTicker]
  );

  const validate = useCallback(
    (formData: TransformedReservation, errors: FormValidation) => {
      const checkIn = formData.priceManagement
        ? new Date(formData.priceManagement.checkIn)
        : new Date();
      const checkOut = formData.priceManagement
        ? new Date(formData.priceManagement.checkOut)
        : new Date();

      if (isBefore(checkOut, checkIn) || isSameDay(checkOut, checkIn)) {
        errors.priceManagement.addError(intl.formatMessage(messages.dateMustBeAfter));
      }

      if (formData.priceManagement && formData.priceManagement.guaranteeAmount < 0) {
        errors.priceManagement.addError(intl.formatMessage(messages.depositMustBeZeroOrMore));
      }

      // if email ends with * is a masked email and must not be validated
      // http://emailregex.com/
      if (
        formData.email == null ||
        (!formData.email.endsWith("*") &&
          !formData.email.match(
            /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
          ))
      ) {
        errors.priceManagement.addError(intl.formatMessage(messages.emailNotValid));
      }

      // if phone starts with * is a masked phone and must not be validated
      if (
        formData.phone &&
        !formData.phone.startsWith("*") &&
        !formData.phone.match(/\+\d\d?\d?\d?\s[0-9]+/i)
      ) {
        errors.priceManagement.addError(intl.formatMessage(messages.phoneNotValid));
      }
      return errors;
    },
    [intl]
  );

  const allowedLocales = usePropertyLocales();
  const formConfiguration: FormConfiguration<TransformedReservation> = useMemo(
    () => ({
      __fieldOrder: [
        "guestInformationSection",
        "email",
        "name",
        "lastName",
        "phone",
        "bookingDetailsSection",
        "placementDate",
        "confirmationId",
        "*",
      ],
      __useProtoStyleArrays: true,
      id: {
        hidden: true,
      },
      ticker: {
        hidden: true,
      },
      checkIn: {
        hidden: true,
      },
      checkOut: {
        hidden: true,
      },
      guestInformationSection: {
        fieldWidget: Section,
        messages: {
          label: defineMessage({
            id: "modification.translation.guestInformationSection",
            defaultMessage: "Guest information",
          }),
        },
      },
      email: {
        messages: {
          label: defineMessage({
            id: "modification.translation.email",
            defaultMessage: "Description",
          }),
        },
      },
      name: {
        messages: {
          label: defineMessage({
            id: "modification.translation.name",
            defaultMessage: "Description",
          }),
        },
      },
      lastName: {
        messages: {
          label: defineMessage({
            id: "modification.translation.lastName",
            defaultMessage: "Description",
          }),
        },
      },
      phone: {
        messages: {
          label: defineMessage({
            id: "modification.translation.phone",
            defaultMessage: "Phone",
          }),
        },
      },
      bookingDetailsSection: {
        fieldWidget: Section,
        messages: {
          label: defineMessage({
            id: "modification.translation.bookingDetailsSection",
            defaultMessage: "Guest information",
          }),
        },
      },
      placementDate: {
        readonly: true,
        messages: {
          label: defineMessage({
            id: "modification.translation.placementDate",
            defaultMessage: "placementDate",
          }),
        },
      },
      confirmationId: {
        readonly: true,
        messages: {
          label: defineMessage({
            id: "modification.translation.confirmationId",
            defaultMessage: "confirmationId",
          }),
        },
      },
      priceManagement: {
        // TODO: This disabled is temporary until the modification of reservations with package improvement is implemented
        fieldWidget: (props) => <ModificationPriceManagement {...props} disabled={isPackage} />,
        messages: {
          label: [
            defineMessage({
              id: "modification.translation.checkIn",
              defaultMessage: "checkIn",
            }),
            defineMessage({
              id: "modification.translation.checkOut",
              defaultMessage: "checkOut",
            }),
          ],
        },
      },
    }),
    [isPackage]
  );
  if (allowedLocales === undefined) {
    return null;
  }
  return (
    <CrudForm<TransformedReservation>
      formConfiguration={formConfiguration}
      schema={schema}
      container={FormContainer}
      id={"modification"}
      messages={messages}
      onGet={onGet}
      onUpdate={onUpdate}
      canUpdate={actions.ModifyReservation}
      validate={validate}
    />
  );
}

export default withAuthorizationVerifier(ReservationModificationForm);
